import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  CreditCard, Loader2, AlertTriangle, CheckCircle2, Lock, Receipt, Play, Save, Search,
} from 'lucide-react';
import { apiErrorMessage } from '@/lib/apiError';
import WebShipImportPanel from '@/components/WebShipImportPanel';
import { formatMoney as money } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const METHODS = [['ach', 'ACH'], ['wire', 'Wire'], ['check', 'Check'], ['cash', 'Cash'], ['other', 'Other']];

const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

async function apiCall(token, path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...opts,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(apiErrorMessage(d, `Request failed (${r.status}).`));
  return d;
}

/**
 * ADMIN ONLY — SelleGard (SmartLGSTX) merchant credit.
 *
 * Who may ship on credit, their limit (1,000 by default), holds, the weekly
 * billing day, and the invoices. Every change to an account asks for a note,
 * because the backend logs who changed what and why.
 */
export default function AdminSelleGardCreditPage() {
  const { token } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(true);

  const [editing, setEditing] = useState(null);   // user_id
  const [form, setForm] = useState({});
  const [paying, setPaying] = useState(null);     // invoice id
  const [payForm, setPayForm] = useState({ method: 'ach', reference: '', note: '' });

  const call = (path, opts) => apiCall(token, path, opts);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, i, al, c] = await Promise.all([
        apiCall(token, '/webship/credit/accounts'),
        apiCall(token, '/webship/credit/invoices'),
        apiCall(token, '/webship/credit/alerts'),
        apiCall(token, '/webship/credit/config'),
      ]);
      setAccounts(a.accounts || []);
      setInvoices(i.invoices || []);
      setAlerts((al.alerts || []).filter((x) => !x.resolved));
      setConfig(c);
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const emailOf = (uid) => accounts.find((a) => a.user_id === uid)?.email || uid;

  const startEdit = (a) => {
    setEditing(a.user_id);
    setForm({ authorized: a.authorized, limit: a.limit, frozen: a.frozen, note: '' });
  };

  const saveAccount = async (a) => {
    setBusy('acct'); setBanner(null);
    try {
      const body = { note: form.note };
      if (form.authorized !== a.authorized) body.authorized = form.authorized;
      if (Number(form.limit) !== Number(a.limit)) body.limit = Number(form.limit);
      if (form.frozen !== a.frozen) body.frozen = form.frozen;
      await call(`/webship/credit/accounts/${a.user_id}`, { method: 'POST', body: JSON.stringify(body) });
      setBanner({ type: 'ok', text: `Saved ${a.email || a.user_id}.` });
      setEditing(null);
      await load();
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  const saveConfig = async () => {
    setBusy('cfg'); setBanner(null);
    try {
      const c = await call('/webship/credit/config', {
        method: 'POST',
        body: JSON.stringify({ billing_weekday: Number(config.billing_weekday), grace_days: Number(config.grace_days) }),
      });
      setConfig(c);
      setBanner({ type: 'ok', text: `Billing day ${c.billing_weekday_name}, ${c.grace_days} days to pay.` });
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  const runBilling = async () => {
    if (!window.confirm('Issue invoices now for everything owed up to this moment? Each merchant with open shipments gets an invoice and an email.')) return;
    setBusy('run'); setBanner(null);
    try {
      const r = await call('/webship/credit/run-billing', { method: 'POST', body: JSON.stringify({ force: true }) });
      const n = r.invoices?.issued?.length || 0;
      setBanner({ type: 'ok', text: n ? `Issued ${n} invoice(s): ${r.invoices.issued.join(', ')}.` : 'Nothing to invoice.' });
      await load();
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  const markPaid = async (inv) => {
    setBusy('pay'); setBanner(null);
    try {
      await call(`/webship/credit/invoices/${inv.id}/mark-paid`, { method: 'POST', body: JSON.stringify(payForm) });
      setBanner({ type: 'ok', text: `${inv.number} marked paid.` });
      setPaying(null);
      setPayForm({ method: 'ach', reference: '', note: '' });
      await load();
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  if (loading && !config) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  }

  const q = search.trim().toLowerCase();
  const shownAccounts = accounts.filter((a) => !q || (a.email || '').toLowerCase().includes(q) || (a.company_name || '').toLowerCase().includes(q));
  const shownInvoices = invoices.filter((i) => !onlyOpen || i.status === 'open');

  return (
    <div className="space-y-6" data-testid="admin-sellegard-credit">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <CreditCard className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">Admin · SelleGard Merchant Credit</h1>
          <p className="text-sm text-slate-500">SmartLGSTX ships on credit for authorized merchants with an approved KYS only. USPS is always prepaid. Weekly itemized invoice; unpaid means on hold.</p>
        </div>
      </div>

      {banner && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${banner.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="credit-admin-banner">
          {banner.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          <span>{banner.text}</span>
        </div>
      )}

      <WebShipImportPanel token={token} accounts={accounts} onChange={load} />

      {alerts.length > 0 && (
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-sm" data-testid="credit-alerts">
          <p className="font-semibold text-rose-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Needs a person</p>
          <ul className="mt-2 space-y-1 text-rose-700">
            {alerts.map((a) => (
              <li key={a.id}>
                {day(a.created_at)} · {a.type === 'voided_after_invoice' ? `WebShip shipment ${a.detail.book_number} was voided after it was invoiced (${a.detail.invoice}). Credit the merchant ${money(a.detail.amount)}.`
                  : a.type === 'autopay_failed' ? `Automatic payment for ${a.detail.invoice} failed: ${a.detail.reason}. The invoice is still open.`
                  : a.type === 'paid_twice' ? `Invoice ${a.detail.invoice} was paid twice by card (session ${a.detail.session_id}). Refund one in Stripe.`
                  : a.type === 'amount_mismatch' ? `Card payment for ${a.detail.invoice} came back with a different amount. Not settled.`
                  : `${a.type}: ${JSON.stringify(a.detail)}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Settings */}
      {config && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="credit-config">
          <h3 className="text-lg font-semibold mb-3">Billing</h3>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Billing day">
              <select className="input" value={config.billing_weekday} onChange={(e) => setConfig({ ...config, billing_weekday: e.target.value })} data-testid="cfg-weekday">
                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </Field>
            <Field label="Days to pay">
              <input type="number" min="0" max="30" className="input" value={config.grace_days} onChange={(e) => setConfig({ ...config, grace_days: e.target.value })} data-testid="cfg-grace" />
            </Field>
            <Button onClick={saveConfig} disabled={busy === 'cfg'} data-testid="cfg-save">
              {busy === 'cfg' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save</>}
            </Button>
            <Button variant="outline" onClick={runBilling} disabled={busy === 'run'} data-testid="run-billing">
              {busy === 'run' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1.5" />Invoice now</>}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Invoices go out automatically every {config.billing_weekday_name} at 16:00 UTC (8 or 9 am in San Diego), for everything booked before that day. Default limit for a new account: {money(config.default_limit)}.
          </p>
        </div>
      )}

      {/* Accounts */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="credit-accounts">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="text-lg font-semibold">Merchants</h3>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <input className="input" style={{ paddingLeft: 32 }} placeholder="Search email or company" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="acct-search" />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-2 text-left">Merchant</th>
              <th className="px-2 py-2 text-left">Credit</th>
              <th className="px-2 py-2 text-right">Limit</th>
              <th className="px-2 py-2 text-right">Owed</th>
              <th className="px-2 py-2 text-right">Available</th>
              <th className="px-2 py-2 text-right">Open inv.</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {shownAccounts.map((a) => (
              <Fragment key={a.user_id}>
                <tr className="border-t border-slate-100 dark:border-slate-800" data-testid={`acct-${a.user_id}`}>
                  <td className="px-2 py-2">
                    <div>{a.email || a.user_id}</div>
                    {a.company_name && <div className="text-xs text-slate-500">{a.company_name}</div>}
                  </td>
                  <td className="px-2 py-2">
                    {!a.authorized ? <span className="text-xs text-slate-400">Not authorized{a.kys_approved ? '' : ' · KYS not approved'}</span>
                      : !a.kys_approved ? <span className="text-xs text-amber-700" data-testid={`kys-missing-${a.user_id}`}>KYS not approved · cannot book</span>
                      : a.frozen ? <span className="text-xs text-rose-600 flex items-center gap-1"><Lock className="w-3 h-3" />On hold · {a.frozen_reason_text}</span>
                      : <span className="text-xs text-emerald-600">Active{a.autopay?.enabled ? ' · autopay' : ''}</span>}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(a.limit)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(a.outstanding)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${a.over_limit ? 'text-rose-600' : ''}`}>{money(a.available)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{a.open_invoices}</td>
                  <td className="px-2 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => (editing === a.user_id ? setEditing(null) : startEdit(a))} data-testid={`edit-${a.user_id}`}>
                      {editing === a.user_id ? 'Close' : 'Edit'}
                    </Button>
                  </td>
                </tr>
                {editing === a.user_id && (
                  <tr className="bg-slate-50 dark:bg-slate-900/60">
                    <td colSpan={7} className="px-2 py-3">
                      <div className="flex flex-wrap items-end gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={form.authorized} onChange={(e) => setForm({ ...form, authorized: e.target.checked })} data-testid="f-authorized" />
                          Authorized for credit
                        </label>
                        {!a.kys_approved && <p className="text-xs text-amber-700 w-full" data-testid="f-kys-missing">KYS is not approved for this merchant. Approve it in the KYS queue before granting credit.</p>}
                        <Field label="Limit (USD)">
                          <input type="number" min="0" step="0.01" className="input w-32" value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })} data-testid="f-limit" />
                        </Field>
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={form.frozen} onChange={(e) => setForm({ ...form, frozen: e.target.checked })} data-testid="f-frozen" />
                          On hold
                        </label>
                        <Field label="Why (required, it is logged)">
                          <input className="input w-72" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. verified, pays every Friday" data-testid="f-note" />
                        </Field>
                        <Button onClick={() => saveAccount(a)} disabled={busy === 'acct' || !form.note.trim()} data-testid="f-save">
                          {busy === 'acct' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                      {a.frozen && !form.frozen && (
                        <p className="text-xs text-amber-700 mt-2">Lifting a hold by hand is remembered: the daily check will not put it back for an invoice that was already overdue. A later overdue invoice will.</p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invoices */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="credit-invoices-admin">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold flex items-center gap-2"><Receipt className="w-5 h-5 text-slate-500" />Invoices</h3>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} data-testid="only-open" />
            Open only
          </label>
        </div>
        {shownInvoices.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No invoices.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2 text-left">Invoice</th>
                <th className="px-2 py-2 text-left">Merchant</th>
                <th className="px-2 py-2 text-left">Issued</th>
                <th className="px-2 py-2 text-left">Due</th>
                <th className="px-2 py-2 text-right">Total</th>
                <th className="px-2 py-2 text-left">Email</th>
                <th className="px-2 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {shownInvoices.map((inv) => (
                <Fragment key={inv.id}>
                  <tr className="border-t border-slate-100 dark:border-slate-800" data-testid={`adm-inv-${inv.number}`}>
                    <td className="px-2 py-2 font-mono text-xs">{inv.number}</td>
                    <td className="px-2 py-2">{emailOf(inv.user_id)}</td>
                    <td className="px-2 py-2">{day(inv.issued_at)}</td>
                    <td className={`px-2 py-2 ${inv.status === 'open' && new Date(inv.due_at) < new Date() ? 'text-rose-600 font-medium' : ''}`}>{day(inv.due_at)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold">{money(inv.total)}</td>
                    <td className="px-2 py-2 text-xs">{inv.email_status === 'sent' ? 'Sent' : <span className="text-amber-700" title={inv.email_error || ''}>Not sent</span>}</td>
                    <td className="px-2 py-2 text-right">
                      {inv.status === 'paid' ? (
                        <span className="text-xs text-emerald-600">Paid {day(inv.paid_at)} · {inv.paid_via}</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setPaying(paying === inv.id ? null : inv.id)} data-testid={`mark-${inv.number}`}>
                          {paying === inv.id ? 'Close' : 'Mark paid'}
                        </Button>
                      )}
                    </td>
                  </tr>
                  {paying === inv.id && (
                    <tr className="bg-slate-50 dark:bg-slate-900/60">
                      <td colSpan={7} className="px-2 py-3">
                        <div className="flex flex-wrap items-end gap-3">
                          <Field label="Method">
                            <select className="input" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} data-testid="p-method">
                              {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                          </Field>
                          <Field label="Reference (ACH trace, check #…)">
                            <input className="input w-56" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} data-testid="p-ref" />
                          </Field>
                          <Field label="Note (optional)">
                            <input className="input w-56" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} data-testid="p-note" />
                          </Field>
                          <Button onClick={() => markPaid(inv)} disabled={busy === 'pay' || !payForm.reference.trim()} data-testid="p-save">
                            {busy === 'pay' ? <Loader2 className="w-4 h-4 animate-spin" /> : `Mark ${money(inv.total)} paid`}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <style>{`.input{width:100%;height:38px;padding:0 10px;border-radius:8px;border:1px solid rgb(226 232 240);font-size:13px;background:transparent;}
        .dark .input{border-color:rgb(51 65 85);}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">{label}</label>
    {children}
  </div>
);
